import {
  SHAPE_KINDS,
  SHAPE_LABELS,
  applyPath,
  shapePath,
  type ShapeKind,
} from '@kanvas/board-core';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, type ToolId } from '../state/store';
import { Icon, type IconName } from './icons';

interface ToolSpec {
  id: ToolId;
  icon: IconName;
  label: string;
  shortcut: string;
}

const TOOLS: ToolSpec[] = [
  { id: 'select', icon: 'cursor', label: 'Выбор', shortcut: 'V' },
  { id: 'hand', icon: 'hand', label: 'Перемещение', shortcut: 'H' },
  { id: 'sticky', icon: 'sticky', label: 'Стикер', shortcut: 'N' },
  { id: 'shape', icon: 'square', label: 'Фигура', shortcut: 'R' },
  { id: 'text', icon: 'text', label: 'Текст', shortcut: 'T' },
  { id: 'draw', icon: 'pen', label: 'Рисование', shortcut: 'P' },
  { id: 'eraser', icon: 'eraser', label: 'Ластик', shortcut: 'E' },
  { id: 'connector', icon: 'arrow', label: 'Соединитель', shortcut: 'L' },
  { id: 'frame', icon: 'frame', label: 'Фрейм', shortcut: 'F' },
  { id: 'notebook', icon: 'notebook', label: 'Тетрадь', shortcut: 'B' },
  { id: 'comment', icon: 'comment', label: 'Комментарий', shortcut: 'C' },
  { id: 'laser', icon: 'laser', label: 'Лазерная указка', shortcut: 'K' },
];

/** Tool ids a commenter link may still use — everything that doesn't touch content. */
const COMMENTER_TOOLS: ReadonlySet<ToolId> = new Set(['select', 'hand', 'comment', 'laser']);

export interface ToolbarProps {
  onUploadImage: () => void;
  /** Trims the palette down to non-editing tools for `commenter` links. */
  commentOnly?: boolean;
}

export function Toolbar({ onUploadImage, commentOnly }: ToolbarProps) {
  const tool = useEditor((s) => s.tool);
  const toolLocked = useEditor((s) => s.toolLocked);
  const shapeKind = useEditor((s) => s.shapeKind);
  const setTool = useEditor((s) => s.setTool);
  const [shapesOpen, setShapesOpen] = useState(false);
  const shapeButtonRef = useRef<HTMLDivElement>(null);
  const shapePickerRef = useRef<HTMLDivElement>(null);
  const tools = commentOnly ? TOOLS.filter((t) => COMMENTER_TOOLS.has(t.id)) : TOOLS;

  const hostRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef(new Map<ToolId, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<{ offset: number; size: number } | null>(null);

  /**
   * The active-tool highlight is one element that slides between buttons,
   * rather than a background toggled per button. Moving a single marker makes
   * the change legible as *movement along the toolbar*, which is what tells the
   * user which way they went — and it animates a transform instead of
   * cross-fading two backgrounds.
   *
   * Positions are measured from the DOM rather than computed from an index:
   * the separator and the shape button's caret break any assumption of uniform
   * spacing, and the toolbar flips to horizontal on small screens.
   */
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const measure = () => {
      const button = buttonRefs.current.get(tool);
      if (!button) {
        setIndicator(null);
        return;
      }
      const horizontal = getComputedStyle(host).flexDirection.startsWith('row');
      const next = horizontal
        ? { offset: button.offsetLeft, size: button.offsetWidth }
        : { offset: button.offsetTop, size: button.offsetHeight };
      setIndicator((prev) =>
        prev && prev.offset === next.offset && prev.size === next.size ? prev : next,
      );
    };

    measure();
    // The toolbar flips between a column and a row at the small-screen
    // breakpoint and grows on touch: either leaves a marker measured for the
    // old layout sitting on the wrong button until the tool changes again.
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, [tool, commentOnly]);

  // Close the shape picker on any outside interaction, and on Escape so a
  // keyboard user is never left with a menu they cannot dismiss.
  useEffect(() => {
    if (!shapesOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The picker is portalled out of the toolbar, so it is checked on its own.
      if (shapeButtonRef.current?.contains(target) || shapePickerRef.current?.contains(target)) return;
      setShapesOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShapesOpen(false);
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [shapesOpen]);

  const register = (id: ToolId) => (el: HTMLButtonElement | null) => {
    if (el) buttonRefs.current.set(id, el);
    else buttonRefs.current.delete(id);
  };

  return (
    <div className="toolbar" role="toolbar" aria-label="Инструменты доски" ref={hostRef}>
      {indicator && (
        <span
          className="toolbar-indicator"
          aria-hidden="true"
          style={
            {
              '--indicator-offset': `${indicator.offset}px`,
              '--indicator-size': `${indicator.size}px`,
            } as React.CSSProperties
          }
        />
      )}

      {tools.map((spec) => {
        const active = tool === spec.id;
        const tip = `${spec.label} · ${spec.shortcut}`;

        if (spec.id === 'shape') {
          return (
            <div className="toolbar-shape" key={spec.id} ref={shapeButtonRef}>
              <button
                type="button"
                ref={register(spec.id)}
                className={`tool-btn ${active ? 'is-active' : ''}`}
                data-tip={`${SHAPE_LABELS[shapeKind]} · ${spec.shortcut}`}
                aria-label={SHAPE_LABELS[shapeKind]}
                aria-pressed={active}
                onClick={() => {
                  if (active) setShapesOpen((v) => !v);
                  else setTool('shape');
                }}
                onDoubleClick={() => setShapesOpen(true)}
              >
                <ShapeGlyph kind={shapeKind} />
              </button>
              <button
                type="button"
                className="tool-caret"
                aria-label="Выбрать фигуру"
                aria-haspopup="menu"
                aria-expanded={shapesOpen}
                onClick={() => setShapesOpen((v) => !v)}
              >
                <Icon name="chevronDown" size={12} />
              </button>
              {shapesOpen && (
                <ShapePicker
                  anchorRef={shapeButtonRef}
                  pickerRef={shapePickerRef}
                  value={shapeKind}
                  onPick={(kind) => {
                    useEditor.getState().setShapeKind(kind);
                    setShapesOpen(false);
                  }}
                />
              )}
            </div>
          );
        }

        return (
          <button
            key={spec.id}
            type="button"
            ref={register(spec.id)}
            className={`tool-btn ${active ? 'is-active' : ''}`}
            data-tip={tip}
            aria-label={spec.label}
            aria-keyshortcuts={spec.shortcut}
            aria-pressed={active}
            onClick={() => setTool(spec.id)}
          >
            <Icon name={spec.icon} />
          </button>
        );
      })}

      {!commentOnly && (
        <>
          <div className="toolbar-sep" />

          <button
            type="button"
            className="tool-btn"
            data-tip="Загрузить изображение · Ctrl U"
            aria-label="Загрузить изображение"
            onClick={onUploadImage}
          >
            <Icon name="image" />
          </button>

          <button
            type="button"
            className={`tool-btn ${toolLocked ? 'is-active' : ''}`}
            data-tip={
              toolLocked ? 'Инструмент закреплён' : 'Закрепить инструмент после использования'
            }
            aria-label="Закрепить инструмент"
            aria-pressed={toolLocked}
            onClick={() => useEditor.getState().toggleToolLock()}
          >
            <Icon name={toolLocked ? 'lock' : 'unlock'} />
          </button>
        </>
      )}
    </div>
  );
}

/** Renders a shape kind as a small preview, reusing the board geometry. */
function ShapeGlyph({ kind, size = 20 }: { kind: ShapeKind; size?: number }) {
  const pad = 2.5;
  const path = shapePath(kind, { x: pad, y: pad, w: size - pad * 2, h: size - pad * 2 }, 3);

  // Build an SVG path string through the same command IR the canvas uses.
  const commands: string[] = [];
  applyPath(path, {
    moveTo: (x, y) => commands.push(`M${round(x)} ${round(y)}`),
    lineTo: (x, y) => commands.push(`L${round(x)} ${round(y)}`),
    quadraticCurveTo: (cx, cy, x, y) =>
      commands.push(`Q${round(cx)} ${round(cy)} ${round(x)} ${round(y)}`),
    bezierCurveTo: (a, b, c, d, x, y) =>
      commands.push(`C${round(a)} ${round(b)} ${round(c)} ${round(d)} ${round(x)} ${round(y)}`),
    closePath: () => commands.push('Z'),
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <path
        d={commands.join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
    </svg>
  );
}

const round = (v: number) => Math.round(v * 100) / 100;

/**
 * Rendered in a portal with fixed positioning. Inside the toolbar it was cut
 * off entirely on small screens: the toolbar scrolls sideways there
 * (`overflow-x: auto`), which clips anything that opens above it.
 */
function ShapePicker({
  anchorRef,
  pickerRef,
  value,
  onPick,
}: {
  anchorRef: RefObject<HTMLDivElement | null>;
  pickerRef: RefObject<HTMLDivElement | null>;
  value: ShapeKind;
  onPick: (kind: ShapeKind) => void;
}) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      const panel = pickerRef.current;
      if (!anchor || !panel) return;
      const a = anchor.getBoundingClientRect();
      const w = panel.offsetWidth;
      const h = panel.offsetHeight;
      const gap = 10;
      const edge = 8;
      // Matches the stylesheet's small-screen breakpoint, where the toolbar
      // becomes a row along the bottom and the picker opens above it.
      const row = window.matchMedia('(max-width: 720px)').matches;
      const left = row ? a.left + a.width / 2 - w / 2 : a.right + gap;
      const top = row ? a.top - gap - h : a.top - 6;
      setPos({
        left: Math.max(edge, Math.min(left, window.innerWidth - w - edge)),
        top: Math.max(edge, Math.min(top, window.innerHeight - h - edge)),
      });
    };
    place();
    window.addEventListener('resize', place);
    // The toolbar itself can scroll sideways on a phone.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, pickerRef]);

  return createPortal(
    <div
      ref={pickerRef}
      className="shape-picker is-floating"
      role="menu"
      aria-label="Фигуры"
      style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden' }}
    >
      {SHAPE_KINDS.map((kind, i) => (
        <button
          key={kind}
          type="button"
          role="menuitemradio"
          aria-checked={kind === value}
          className={`shape-picker-item ${kind === value ? 'is-active' : ''}`}
          aria-label={SHAPE_LABELS[kind]}
          // Staggering the reveal reads as the grid assembling itself; the
          // delay is capped so the last tile is never a noticeable wait.
          style={{ animationDelay: `${Math.min(i, 14) * 12}ms` }}
          onClick={() => onPick(kind)}
        >
          <ShapeGlyph kind={kind} size={24} />
        </button>
      ))}
    </div>,
    document.body,
  );
}
